/**
 * step-update.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { TypedData } from "../../../common/data/typed-data";
import { Comment } from "../../../intake-form/comment";
import { Identifiable } from "../../../support/types/aliases";
import { assignExcept } from "../../../support/utilities/functions";
import { AlgorithmStep, IntakeAnswer, IntakeStep, ReportStep }
  from "../../structure";

import { TransformerEffects } from "./transformer-effects";
import { TransformerService } from "./transformer.service";

/**
 * A {@link StepUpdate} encodes a generic change being applied to an algorithm
 * entity, usually an {@link AlgorithmStep}, but encoded generally to allow for
 * extra flexibility in its operation.
 */
export abstract class StepUpdate<T extends Identifiable, U extends Identifiable>
{
  /**
   * Whether the update is permitted to create new steps in the registry, or
   * if it should throw an error when called on an unknown step.
   */
  public readonly canCreate : boolean = false;

  /**
   * The id of the source object.
   */
  public id : number;

  /**
   * Create a new {@link StepUpdate}
   */
  constructor (public source : T)
  {
    this.id = source.id;
  }

  /**
   * Is a particular receiving object a valid target for this specific update?
   * By default this is implemented via a direct identifier match between source
   * and target, but may be overridden by child classes.
   */
  public matches (target : U) : boolean
  {
    return target.id === this.id;
  }

  /**
   * Whether this update generically matches a class.
   */
  public abstract matchesType (type : any) : boolean;

  /**
   * Run the update operation upon the target.
   */
  public abstract apply (target : U) : void;

  /**
   * Shortcut for finding a valid target within an array and applying the update
   * to it.
   */
  public findAndApply (target : U[]) : boolean
  {
    const result : U = target.find((el : U) => this.matches(el));
    if (result)
    {
      this.apply(result);
      return true;
    }
    return false;
  }

  /**
   * Alternative to findAndApply for accessing an id->step map. (Otherwise we'd
   * have needless conversions back and forth between maps and arrays.)
   */
  public getAndApply (target : Map<number, U>) : boolean
  {
    const result : U = target.get(this.id);
    if (result)
    {
      this.apply(result);
      return true;
    }
    return false;
  }

  /**
   * Create a new entry based on the data in this update.
   */
  public create () : U
  {
    console.error(this);
    throw new Error("Cannot use this update type to create entry!");
  }
}

/**
 * A {@link StepDefinition} is a {@link StepUpdate} containing a new or modified
 * definition of an {@link AlgorithmStep}, enumerating all of its properties,
 * values, and metadata. This is the primary means by which steps are created.
 */
export class StepDefinition<T extends AlgorithmStep> extends StepUpdate<T, T>
{
  /**
   * Create a new {@link StepDefinition} from the step, and an optional override
   * for the default control builder.
   */
  public constructor (step : T, private transformer : TransformerService)
  {
    super(step);
  }

  // Definitions CAN create new steps
  public readonly canCreate : boolean = true;

  public effects : TransformerEffects;

  /**
   * Apply the definition to an existing step
   */
  public apply (target : T) : void
  {
    // We assign (and overwrite) all properties of the target step with the new
    // definition EXCEPT ones that would change the data shape. In practice this
    // should never happen anyway, but safety isn't bad. Really we should throw
    // a huge error if any of them appear different (TODO!).
    assignExcept(
      target,
      this.source,
      ["type", "cardinality", "hints", "id", "value"]
    );
    // (in practice, the only things that can currently change are value,
    // isActive, and presentation)

    if (target instanceof IntakeStep && this.source instanceof IntakeStep)
    {
      if (target.value !== this.source.value)
      {
        target.value = this.source.value;
        this.effects = this.transformer.run(target);
        this.effects.modified_intake_value = true;
      }
      else
      {
        this.effects = this.transformer.run(target);
      }
    }
  }

  /**
   * Create an {@link AlgorithmStep} from this definition.
   */
  public create () : T
  {
    if (this.source instanceof IntakeStep)
    {
      // for intake steps, we must create their form controls
      this.effects = this.transformer.run(this.source, true);
    }

    // Currently, we just rely on the "source" algorithm step, which has already
    // been created somewhere (probably directly from websocket). Since it's
    // mostly a data-holding type, we don't really have to construct anything.
    return this.source;
  }

  public matchesType (type : any) : boolean
  {
    return (this.source instanceof type);
  }
}

/**
 * A comment bundle is an object with an id and a bunch of commments, duh!
 */
type CommentBundle = {comments : Comment[]} & Identifiable;

/**
 * A {@link CommentChange} represents any edit or update to the comments of an
 * {@link IntakeStep}.
 */
export class CommentChange extends StepUpdate<CommentBundle, IntakeStep>
{
  /**
   * Create a new {@link CommentChange} from the comment list.
   */
  public constructor (id : number, comments : Comment[])
  {
    super({id, comments});
  }

  /**
   * Update the comments of the target with a simple assignment.
   */
  public apply (target : IntakeStep) : void
  {
    target.comments = this.source.comments;
  }

  public matchesType (type : any) : boolean
  {
    return (type === IntakeStep);
  }
}

/**
 * A {@link StepFocus} is a step-based event for UI consumption, possibly
 * through scrolling, highlighting, or field focus interactions.
 */
export interface StepFocus {
  id : number;
  reason : string;
  duration? : number;
}
/**
 * The default amount of time for focus display modifiers to last.
 * @devConfig
 */
const DEFAULT_HIGHLIGHT_DURATION : number = 4000;

/**
 * A {@link Focalization} is an update suggesting that attention is drawn to an
 * {@link AlgorithmStep} due to a related event (which may or may not cause its
 * own updates to the step).
 */
export class Focalization extends StepUpdate<StepFocus, AlgorithmStep>
{
  /**
   * Apply the focalization, and then after some time, remove it.
   */
  public apply (target : IntakeStep) : void
  {
    target.hints = Object.assign({}, target.hints, {highlighted: true});
    setTimeout(
      () => target.hints.highlighted = false,
      this.source.duration || DEFAULT_HIGHLIGHT_DURATION
    );
  }

  public matchesType (type : any) : boolean
  {
    return (type === IntakeStep);
  }
}

/**
 * A {@link ReceivedAnswer} represents an externally-provided update to an
 * {@link IntakeStep}'s value.
 */
export class ReceivedAnswer extends StepUpdate<IntakeAnswer, IntakeStep>
{
  /**
   * Create a new {@link ReceivedAnswer} from the step, and an optional override
   * for the default control builder.
   */
  public constructor (
    answer : IntakeAnswer,
    private transformer : TransformerService
  )
  {
    super(answer);
  }

  /**
   * Apply the new value to the target {@link IntakeStep}.
   */
  public apply (target : IntakeStep) : void
  {
    // serialize the new value against the source step's metadata
    target.value = TypedData.deserialize(
      Object.assign({}, target, this.source));
    this.transformer.run(target);
  }

  public matchesType (type : any) : boolean
  {
    return (type === IntakeStep);
  }
}

// A step that is visible on the client
type PresentedStep = IntakeStep | ReportStep;

/**
 * A {@link StepDeactivation} disables a step and removes it from view
 */
export class StepDeactivation<T extends PresentedStep>
  extends StepUpdate<any, T>
{
  /**
   * Mark the step as inactive
   */
  public apply (target : PresentedStep) : void
  {
    target.isActive = false;
  }

  public matchesType (type : any) : boolean
  {
    return true;
  }
}

/**
 * A {@link GlobalDeactivation} is a special event that disables all currently
 * known steps.
 */
export class GlobalDeactivation extends StepUpdate<any, any>
{
  /**
   * Being global, this update matches ANY steps.
   */
  public matches () : boolean
  {
    return true;
  }

  /**
   * Mark the step as inactive
   */
  public apply (target : AlgorithmStep) : void
  {
    // TODO: avoid tagging any ComputationSteps as inactive
    (target as PresentedStep).isActive = false;
  }

  /**
   * Find and apply maps the operation to all steps in the input list
   */
  public findAndApply (target : AlgorithmStep[]) : boolean
  {
    target.forEach((step : AlgorithmStep) => this.apply(step));
    return true;
  }

  /**
   * Find and apply maps the operation to all steps in the input list
   */
  public getAndApply (target : Map<number, AlgorithmStep>) : boolean
  {
    target.forEach((step : AlgorithmStep) => this.apply(step));
    return true;
  }

  public matchesType (type : any) : boolean
  {
    return true;
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/algorithms/management/pipeline/step-update.ts